import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import ContentContainer from 'components/content-container';
import { Button, Icon, Text } from 'components/@tedui';
import { useState } from 'react';
import { isValidEmail } from 'lib/util';
import { MASTER_LIST } from 'lib/hooks/useSailthru/const';
import { getOptinDate } from 'lib/hooks/useSailthru/utils';
import { Honeypot } from 'components/footer/Honeypot';
import cx from 'classnames';

/**
 * Props for `Newsletter`.
 */
export type NewsletterProps =
  SliceComponentProps<Content.DubbingNewsletterSlice>;

/**
 * Component for "Newsletter" Slices.
 */
const Newsletter = ({ slice }: NewsletterProps): JSX.Element => {
  const [email, setEmail] = useState('');
  const [honeypotChecked, setHoneypotChecked] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const onSubmit = () => {
    const isValid = isValidEmail(email);

    if (!isValid) {
      return;
    }

    signUp({
      [slice.primary.optin_date_value]: getOptinDate(),
      [slice.primary.source]: slice.primary.source_value
    });
  };

  const signUp = (vars: { [key: string]: string }) => {
    if (honeypotChecked) return;

    if (window.Sailthru === undefined) {
      console.error('Sailthru is not defined');
      return;
    }

    window.Sailthru.integration('userSignUp', {
      email,
      lists: {
        ...MASTER_LIST,
        [slice.primary.list_name]: 1
      },
      vars: vars,
      source: 'web',
      onSuccess: () => {
        setEmail('');

        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      },
      onError: () => {
        console.error('Error subscribing to newsletter');
      }
    });
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <ContentContainer className="bg-gray-300">
        <div className="h-0">
          <Honeypot onGrab={setHoneypotChecked} />
        </div>
        <div className="mb-6 mt-14 h-px w-full bg-black" />
        <div className="bg-gray-100 flex flex-col justify-between gap-6 pb-20 md-tui:gap-10 lg-tui:flex-row lg-tui:gap-6">
          {showSuccess ? (
            <Text variant="header2" UNSAFE_className="flex-1" useNewTextStyles>
              Thanks for subscribing!
            </Text>
          ) : (
            <Text variant="header2" UNSAFE_className="flex-1" useNewTextStyles>
              {slice.primary.title}
            </Text>
          )}
          <div className="flex flex-1 flex-col gap-6">
            <Text useNewTextStyles variant="subheader1">
              {slice.primary.side_text}
            </Text>

            <div className="flex w-full flex-col items-center gap-4 md-tui:flex-row md-tui:items-start">
              <div className="flex w-full flex-col md-tui:w-[341px] lg-tui:w-full">
                <div className="relative w-full">
                  <input
                    onChange={e => {
                      const isEmailValid = isValidEmail(e.target.value);
                      setIsValid(isEmailValid);
                      setEmail(e.target.value);
                    }}
                    placeholder="email@domain.com"
                    id="newsletter-email"
                    className={cx(
                      'flex h-12 w-full flex-1 border-thin bg-white p-4 text-sm focus:outline-none',
                      {
                        'border-systemInfo-error-onLight focus:border-thin focus:border-systemInfo-error-onLight':
                          !isValid,
                        'border-systemInfo-success focus:border-systemInfo-success':
                          isValid && email.length > 0
                      }
                    )}
                    type="email"
                    value={email}
                  />
                  {!isValid && (
                    <Icon
                      iconName="alert-triangle"
                      className="absolute right-2 top-1/2 mr-2 -translate-y-1/2 transform text-tui-xl text-systemInfo-error-onDark"
                    />
                  )}
                </div>

                {!isValid && (
                  <Text
                    tag="p"
                    variant="caption"
                    UNSAFE_className="mt-1 ml-2"
                    color={{
                      color: 'error'
                    }}
                  >
                    <span aria-live="polite">
                      Please add a valid email address
                    </span>
                  </Text>
                )}
              </div>
              <Button
                variant="primary"
                text="Subscribe Now"
                UNSAFE_className="md-tui:min-w-35 min-w-full !h-12"
                isFullWidth={false}
                onClick={() => onSubmit()}
              />
            </div>
          </div>
        </div>
      </ContentContainer>
    </section>
  );
};

export default Newsletter;
